<script setup lang="ts">
/**
 * A person's actions on one work item (#1831), on the board card and on the item's page: approve or decline a
 * proposed item (declining asks for a reason, which the proposing task reads on the item), move it to the top, up,
 * down or to the bottom of its list, put it on hold or release it, and drop it. Each is one call to the API, kept
 * in the item's history with the person who took it; the parent reads again on `changed`. An item a run is working
 * cannot be moved: hold it first, which takes it from the run.
 */
import { ArrowDown, ArrowUp, Check, ChevronsDown, ChevronsUp, Pause, Play } from '@lucide/vue'
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Textarea,
    ZhConfirmButton,
} from '@zerohuman/ui'
import { computed, ref } from 'vue'
import { api } from '@/api'

const props = defineProps<{
    item: { id: string; title: string; state: string; claimed: boolean }
    /** On a card: icon moves and short labels. */
    compact?: boolean
}>()
const emit = defineEmits<{ changed: [] }>()

const busy = ref(false)
const error = ref<string | null>(null)
const declining = ref(false)
const declineReason = ref('')

const open = computed(() => ['proposed', 'ready', 'held', 'in_progress'].includes(props.item.state))
const proposed = computed(() => props.item.state === 'proposed')
const held = computed(() => props.item.state === 'held')
/** Only an unclaimed open item has a place in the list to move; a run's item is where the run is. */
const movable = computed(() => open.value && props.item.state !== 'in_progress')
const holdable = computed(() => open.value && !held.value)

async function act(work: () => Promise<unknown>, failure: string): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    try {
        await work()
        emit('changed')
    } catch (caught) {
        const code = caught instanceof Error && caught.message ? ` (${caught.message})` : ''
        error.value = `${failure}${code}`
    } finally {
        busy.value = false
    }
}

const approve = () => act(() => api.updateWorkItem(props.item.id, { state: 'ready' }), 'Could not approve the item')
const hold = () => act(() => api.updateWorkItem(props.item.id, { state: 'held' }), 'Could not put the item on hold')
const release = () => act(() => api.updateWorkItem(props.item.id, { state: 'ready' }), 'Could not release the item')
const drop = () => act(() => api.closeWorkItem(props.item.id, { state: 'dropped' }), 'Could not drop the item')
const move = (to: 'top' | 'up' | 'down' | 'bottom') =>
    act(() => api.moveWorkItem(props.item.id, to), 'Could not move the item')

async function decline(): Promise<void> {
    const reason = declineReason.value.trim()
    if (!reason) return
    await act(() => api.closeWorkItem(props.item.id, { state: 'dropped', reason }), 'Could not decline the item')
    if (!error.value) {
        declining.value = false
        declineReason.value = ''
    }
}
</script>

<template>
    <div v-if="open" class="space-y-2" data-testid="work-item-actions">
        <div class="flex flex-wrap items-center gap-1.5">
            <Button v-if="proposed" type="button" size="sm" :disabled="busy" data-testid="work-item-approve" @click="approve"><Check :size="14" /> Approve</Button>
            <Button v-if="proposed" type="button" size="sm" variant="outline" :disabled="busy" data-testid="work-item-decline" @click="declining = true">Decline</Button>
            <template v-if="movable">
                <Button type="button" size="icon-xs" variant="ghost" :disabled="busy" aria-label="Move to the top" data-testid="work-item-move-top" @click="move('top')"><ChevronsUp :size="14" /></Button>
                <Button type="button" size="icon-xs" variant="ghost" :disabled="busy" aria-label="Move up" data-testid="work-item-move-up" @click="move('up')"><ArrowUp :size="14" /></Button>
                <Button type="button" size="icon-xs" variant="ghost" :disabled="busy" aria-label="Move down" data-testid="work-item-move-down" @click="move('down')"><ArrowDown :size="14" /></Button>
                <Button type="button" size="icon-xs" variant="ghost" :disabled="busy" aria-label="Move to the bottom" data-testid="work-item-move-bottom" @click="move('bottom')"><ChevronsDown :size="14" /></Button>
            </template>
            <Button v-if="holdable" type="button" size="sm" variant="outline" :disabled="busy" data-testid="work-item-hold" @click="hold"><Pause :size="14" /> {{ item.claimed ? 'Hold (takes it from the run)' : 'Hold' }}</Button>
            <Button v-if="held" type="button" size="sm" variant="outline" :disabled="busy" data-testid="work-item-release" @click="release"><Play :size="14" /> Release</Button>
            <ZhConfirmButton
                label="Drop"
                :title="`Drop ${item.title}?`"
                description="The item leaves the list and the board. Its page and history stay, and nothing already done is undone."
                confirm-label="Drop it"
                size="sm"
                :disabled="busy"
                :aria-label="`Drop ${item.title}`"
                @confirm="drop"
            />
        </div>
        <p v-if="error" role="alert" class="text-destructive text-xs">{{ error }}</p>
        <Dialog v-model:open="declining">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Decline {{ item.title }}?</DialogTitle>
                    <DialogDescription>Say why. The task that proposed it reads the reason on the item, and the item is dropped.</DialogDescription>
                </DialogHeader>
                <Textarea v-model="declineReason" rows="3" aria-label="Reason for declining" data-testid="work-item-decline-reason" />
                <DialogFooter>
                    <Button type="button" size="sm" variant="outline" @click="declining = false">Cancel</Button>
                    <Button type="button" size="sm" :disabled="busy || declineReason.trim() === ''" data-testid="work-item-decline-confirm" @click="decline">Decline</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>
